import { ExploreInstructorDT } from "@templates/new-explore/hooks/types";
import { ExploreCategory } from "@modules/layout/modal/explore/category";
import {
  exploreFilterSchema,
  filterSelectorPerSlug,
  oldFilterConverter,
} from "@modules/new-explore/schema";
import {
  ExploreFilterItem,
  ExploreFilterSchemaFields,
  ExploreFilterSchemaType,
} from "@modules/new-explore/types";
import { COURSE_TYPE } from "@types";
import { EXPLORE_BOUGHT } from "enums/new-explore/explore-bought";
import { EXPLORE_COST } from "enums/new-explore/explore-cost";
import {
  EXPLORE_SORT,
  normalizeExploreSortQuery,
} from "enums/new-explore/explore-sort";

import { ParsedUrlQuery } from "querystring";
import { EXPLORE_SLUG, exploreSortItems } from "types/api/explore";
import {
  NewExploreCoursesResponse,
  NewExploreInstructorsResponse,
  NewExploreLandingsResponse,
} from "types/api/new-explore";
import { ActiveFilterType } from "@templates/new-explore/hooks/useExloreStore";

const isValidFilter = (filterName: string) => {
  return [
    "slug",
    "term",
    "category_id",
    "type",
    "duration",
    "cost",
    "publish_status",
    "sort",
    "page",
    "sku",
    "instructor_id",
    "certificate",
    "bought",
  ].includes(filterName);
};

const isOldValidFilter = (filterName: string) => {
  return [
    "keyword",
    "category_ids",
    "is_free",
    "status",
    "orderby",
    "instructor_ids",
    "display",
  ].includes(filterName);
};
export const costConverter: { [key: string]: EXPLORE_COST } = {
  "0": EXPLORE_COST.FREE,
  "1": EXPLORE_COST.NOT_FREE,
};

export const adaptPayload = (query: ParsedUrlQuery) => {
  const normalizedQuery = Object.fromEntries(
    Object.entries(query)
      .filter(([key]) => key !== "amp")
      .map(([key, value]) => [key.replace(/^amp;/, ""), value])
  );
  let adaptedPayload = Object.entries(normalizedQuery).reduce(
    (acc, [key, value]) => {
      if (!isOldValidFilter(key)) {
        acc[key] = value;
      }

      // Searching by specific SKU overrides sorting to 'sku'
      if (key === "sku") {
        acc[key] = typeof value === "string" ? value.toLowerCase() : value;
        acc.sort = "sku";
      }

      if (key === "sort") {
        const normalizedSort = normalizeExploreSortQuery(String(value));
        const sortItem = exploreSortItems.find(
          (sort) => sort.query === normalizedSort || sort.name === value
        );
        if (sortItem) {
          acc.sort = sortItem.name;
        }
      }

      if (isOldValidFilter(key)) {
        const converter = oldFilterConverter[key];
        if (converter) {
          acc[converter.key] = converter.value?.[String(value)] || value;
          acc.isOld = true;
        }
      }

      if (key === "cost") {
        const stringValue = String(value);
        if (stringValue === "0" || stringValue === "1") {
          acc[key] = costConverter[stringValue] || value;
          acc.isOld = true;
        }
      }

      return acc;
    },
    {} as any
  );

  // Default sort fallback if 'sort' is not provided in URL query:
  // - Course page defaults to 'most_sold' (popular)
  // - Other pages (landing/instructor) default to 'newest'
  if (!adaptedPayload.sort) {
    const slug =
      typeof query.slug === "string"
        ? (query.slug as EXPLORE_SLUG)
        : EXPLORE_SLUG.COURSE;

    if (slug === EXPLORE_SLUG.COURSE) {
      adaptedPayload.sort = EXPLORE_SORT.MOST_SOLD;
    } else {
      adaptedPayload.sort = EXPLORE_SORT.NEWEST;
    }
  }
  adaptedPayload.count = 24;

  if (adaptedPayload.bought === EXPLORE_BOUGHT.INCLUDE) {
    adaptedPayload.bought = "";
  } else adaptedPayload.bought = EXPLORE_BOUGHT.EXCLUDE;
  return adaptedPayload;
};

export const prepareFilterSchema = (payload: any, slug: EXPLORE_SLUG) => {
  const filterData = Object.entries(exploreFilterSchema)
    .filter(([key]) => filterSelectorPerSlug[slug].includes(key))
    .reduce((prev, [key, value]) => {
      if (payload.type?.includes(COURSE_TYPE.EXAM)) {
        //Check if the selected exam , published-status and duration filter are hidden.
        if (key === "duration" || key === "publish_status") return prev;
      }
      return {
        ...prev,
        [key]: {
          ...value,
          //check if exist in query ...accordion open by default
          active:
            (value!.data !== undefined
              ? value.data
                  .map((d: ExploreFilterItem<any>) => d.name)
                  .some((item: string) => payload[key]?.includes(item))
              : false) || value.active,
          data: (value as { data: ExploreFilterItem<any>[] })?.data?.map(
            (fItm) => {
              return fItm;
            }
          ),
        },
      };
    }, {} as ExploreFilterSchemaType);
  return filterData;
};
export const iterateCategory = (
  parentId: number | null,
  list: ExploreCategory[] | ExploreCategory | undefined,
  selected: string[]
) => {
  let activeFilter: {
    label: string;
    id: string;
    value: number;
    parentId: number | null;
  }[] = [];
  if (Array.isArray(list)) {
    for (let itm of list) {
      activeFilter = activeFilter.concat(
        iterateCategory(parentId, itm, selected)
      );
    }
  } else if (selected.includes(String(list?.id))) {
    activeFilter.push({
      value: 0,
      label: list?.name || "",
      id: String(list?.id),
      parentId: parentId,
    });
  }
  if (list && !Array.isArray(list) && list.childs?.length) {
    for (let child of list.childs) {
      activeFilter = activeFilter.concat(
        iterateCategory(list.id, child, selected)
      );
    }
  }

  return activeFilter;
};

export const prepareActiveFilter = (
  payload: any,
  exploreData:
    | NewExploreLandingsResponse
    | NewExploreCoursesResponse
    | NewExploreInstructorsResponse
    | undefined,
  instructors: ExploreInstructorDT[],
  categoryData:
    | {
        data: ExploreCategory[];
      }
    | undefined
) => {
  const excludedKeys = [
    "sort",
    "page",
    "slug",
    "category_id",
    "sku",
    "isOld",
    "count",
  ];

  const updatedFilters = Object.entries(payload)
    .filter(([key]) => !excludedKeys.includes(key) && isValidFilter(key))
    .reduce((acc, [key, value]) => {
      const currentValue =
        typeof value === "string" && value.includes(",")
          ? value.split(",")
          : value;

      const filterSchema =
        exploreFilterSchema[key as ExploreFilterSchemaFields];
      const filterData =
        filterSchema?.data ??
        (key !== "instructor_id"
          ? exploreData?.filters?.[key as ExploreFilterSchemaFields]
          : instructors);

      const mapFilterValues = (val: string) => {
        const currentItem = (
          (filterData as ExploreFilterItem<String>[]) || []
        )?.find?.((d) => d.name === val || d.id == val || d.name.includes(val));

        return {
          label: currentItem?.persian_name,
          id: key === "instructor_id" ? currentItem?.id : currentItem?.name,
        };
      };

      const filterValue = Array.isArray(currentValue)
        ? currentValue.map(mapFilterValues)
        : [mapFilterValues(currentValue as string)];

      // Remove duplicates based on `label`
      const uniqueFilterValue = filterValue.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.label === item.label)
      );

      return {
        ...acc,
        [key]: {
          filter_label: filterSchema?.label,
          filter_value: uniqueFilterValue,
        },
      };
    }, {});

  const selected = payload["category_id"]?.split(",") || [];
  if (selected.length && categoryData?.data) {
    const updatedCategories = iterateCategory(
      null,
      categoryData?.data,
      selected as string[]
    );

    return {
      ...(updatedFilters as ActiveFilterType),
      category_id: {
        filter_label: "دسته‌بندی",
        filter_value: updatedCategories,
      },
    };
  } else {
    return updatedFilters as ActiveFilterType;
  }
};
